import { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/helpers";
import { toast } from "sonner";
import {
    Users, Plug, EnvelopeSimple, PaperPlaneTilt, Plus, Pencil, KeyReturn,
    Trash, Copy, Warning, CheckCircle, Broadcast, EnvelopeOpen, Cookie,
    ArrowsClockwise, Handshake, Heartbeat,
} from "@phosphor-icons/react";

const ROLE_LABEL = {
    admin: "Admin",
    traveler_support: "Traveler Support",
    guardia: "Guardia 24/7",
    sales: "Sales",
    viewer: "Viewer",
};
const ROLE_OPTIONS = Object.keys(ROLE_LABEL);

function AdminShell({ children }) {
    const location = useLocation();
    const tabs = [
        { to: "/admin/users",           label: "Usuarios",           icon: Users,          id: "users" },
        { to: "/admin/channels",        label: "Canales",            icon: Plug,           id: "channels" },
        { to: "/admin/first-touches",   label: "Primeras convs.",    icon: Handshake,      id: "first-touches" },
        { to: "/admin/duplicate-trips", label: "Duplicados",         icon: Copy,           id: "duplicate-trips" },
        { to: "/admin/email-log",       label: "Log de mensajes",    icon: EnvelopeSimple, id: "email-log" },
        { to: "/admin/reliability",     label: "Reliability",        icon: Heartbeat,      id: "reliability" },
        { to: "/admin/simulate",        label: "Simular inbound",    icon: PaperPlaneTilt, id: "simulate" },
    ];
    return (
        <div className="h-full flex flex-col bg-slate-50">
            <TopBar />
            <div className="border-b border-slate-200 bg-white px-6 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
                {tabs.map(t => {
                    const active = location.pathname.startsWith(t.to);
                    const Icon = t.icon;
                    return (
                        <Link
                            key={t.to}
                            to={t.to}
                            data-testid={`admin-tab-${t.id}`}
                            className={
                                "h-12 px-4 inline-flex items-center gap-2 text-sm font-medium border-b-2 transition-colors shrink-0 " +
                                (active
                                    ? "text-brand-700 border-brand-600"
                                    : "text-slate-500 border-transparent hover:text-slate-800")
                            }
                        >
                            <Icon size={16}/> {t.label}
                        </Link>
                    );
                })}
            </div>
            <div className="flex-1 overflow-auto vh-scroll p-4 sm:p-6">{children}</div>
        </div>
    );
}

// ============================ USERS ============================

function UserDialog({ open, onOpenChange, mode, initial, onCreated, onSaved }) {
    // For "create" we no longer ask the admin for a password — the backend
    // generates a 16-char temporary one which we surface on onCreated.
    const [form, setForm] = useState({
        email: "", name: "", role: "viewer",
        avatar_color: "",
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (mode === "edit" && initial) {
            setForm({
                email: initial.email, name: initial.name,
                role: initial.role,
                avatar_color: initial.avatar_color || "", active: initial.active,
            });
        } else if (open && mode === "create") {
            setForm({ email: "", name: "", role: "viewer",
                      avatar_color: "", active: true });
        }
    }, [open, mode, initial]);

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            if (mode === "create") {
                const payload = {
                    email: form.email.trim().toLowerCase(),
                    name: form.name.trim(),
                    // password omitted → backend auto-generates and returns it
                    role: form.role,
                    avatar_color: form.avatar_color || null,
                };
                const { data } = await api.post("/users", payload);
                toast.success("Usuario creado");
                onCreated?.(data);   // { user, generated_password }
            } else {
                const payload = {
                    name: form.name.trim(),
                    role: form.role,
                    avatar_color: form.avatar_color || null,
                    active: form.active,
                };
                await api.patch(`/users/${initial.id}`, payload);
                toast.success("Usuario actualizado");
                onSaved?.();
            }
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="user-dialog">
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Nuevo usuario" : `Editar ${initial?.name || ""}`}</DialogTitle>
                    <DialogDescription>
                        {mode === "create"
                            ? "Se generará una contraseña temporal segura que verás una única vez al terminar. El usuario deberá cambiarla al iniciar sesión por primera vez."
                            : "Los cambios se guardan al pulsar Guardar. Para resetear la contraseña usa el botón «Password» de la tabla."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input
                            type="email"
                            value={form.email}
                            disabled={mode === "edit"}
                            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                            data-testid="user-form-email"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Nombre completo</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            data-testid="user-form-name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Rol</Label>
                        <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                            <SelectTrigger data-testid="user-form-role-trigger"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                {ROLE_OPTIONS.map(r => (
                                    <SelectItem key={r} value={r} data-testid={`user-form-role-${r}`}>{ROLE_LABEL[r]}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Color de avatar (hex, opcional)</Label>
                        <Input
                            placeholder="#0F4C81"
                            value={form.avatar_color}
                            onChange={(e) => setForm(f => ({ ...f, avatar_color: e.target.value }))}
                            data-testid="user-form-color"
                        />
                    </div>
                    {mode === "edit" && (
                        <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                            <div>
                                <Label className="block">Activo</Label>
                                <div className="text-xs text-slate-500">Desactivar lo retira de las conversaciones asignadas.</div>
                            </div>
                            <Switch
                                checked={form.active}
                                onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))}
                                data-testid="user-form-active"
                            />
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="user-form-cancel">Cancelar</Button>
                    <Button onClick={onSubmit} disabled={submitting} className="bg-brand-600 hover:bg-brand-700"
                            data-testid="user-form-submit">
                        {submitting ? "Guardando…" : (mode === "create" ? "Crear y generar contraseña" : "Guardar")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Confirmation dialog for the destructive reset-password action. The admin
 * doesn't type anything: they just confirm, backend generates the temporary
 * password and returns it once. The caller then opens GeneratedPasswordDialog.
 */
function ResetPasswordDialog({ open, onOpenChange, user, onGenerated }) {
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/users/${user.id}/reset-password`, {});
            toast.success(`Nueva contraseña temporal generada para ${user.name}`);
            onGenerated?.(data);   // { user, generated_password }
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error reseteando contraseña");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="reset-password-dialog">
                <DialogHeader>
                    <DialogTitle>Resetear contraseña — {user?.name}</DialogTitle>
                    <DialogDescription>
                        Se generará una nueva contraseña temporal segura. <strong>{user?.name}</strong> deberá cambiarla al iniciar sesión.
                        Su sesión actual seguirá activa hasta que expire; entrega la nueva contraseña por un canal seguro (no email plano).
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    <Button onClick={onSubmit} disabled={submitting} className="bg-brand-600 hover:bg-brand-700"
                            data-testid="reset-password-submit">
                        {submitting ? "Generando…" : "Generar contraseña temporal"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * One-time password reveal after a successful create/reset. Cannot be closed
 * by clicking outside (destructive UX safety) — only via the explicit
 * "He guardado la contraseña" button. Shows the plaintext password in a
 * monospaced pill with a copy button.
 */
function GeneratedPasswordDialog({ payload, onClose }) {
    const [copied, setCopied] = useState(false);
    const open = !!payload;
    const pwd = payload?.generated_password || "";
    const user = payload?.user;

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(pwd);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
            toast.success("Contraseña copiada al portapapeles");
        } catch {
            toast.error("No se pudo copiar. Selecciónala y copia manualmente.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent
                data-testid="generated-password-dialog"
                className="max-w-lg"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                            <KeyReturn size={18} weight="bold" />
                        </div>
                        <div>
                            <DialogTitle>Contraseña temporal generada</DialogTitle>
                            <DialogDescription>
                                Para <strong>{user?.name}</strong> · <code className="text-xs">{user?.email}</code>
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900"
                     data-testid="generated-password-warning">
                    <Warning size={18} weight="fill" className="shrink-0 text-amber-600 mt-0.5"/>
                    <div>
                        <div className="font-semibold">Esta contraseña <u>no se volverá a mostrar</u>.</div>
                        <div className="text-xs mt-0.5">Cópiala ahora y compártela con el usuario por un canal seguro (chat interno, cara a cara, gestor de contraseñas). Al iniciar sesión el usuario tendrá que cambiarla obligatoriamente.</div>
                    </div>
                </div>

                <div className="mt-2">
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 block">
                        Contraseña
                    </Label>
                    <div className="flex items-center gap-2">
                        <code
                            data-testid="generated-password-value"
                            className="flex-1 select-all font-mono text-lg font-semibold bg-slate-900 text-emerald-300 px-3 py-2.5 rounded tracking-wider break-all"
                        >
                            {pwd}
                        </code>
                        <Button
                            onClick={onCopy}
                            variant="outline"
                            className="shrink-0 h-11"
                            data-testid="generated-password-copy-btn"
                        >
                            {copied
                                ? <CheckCircle size={16} weight="fill" className="text-emerald-600"/>
                                : <Copy size={16}/>}
                            <span className="ml-1.5">{copied ? "Copiado" : "Copiar"}</span>
                        </Button>
                    </div>
                </div>

                <DialogFooter className="mt-2">
                    <Button
                        onClick={onClose}
                        className="bg-brand-600 hover:bg-brand-700 w-full"
                        data-testid="generated-password-ack"
                    >
                        He guardado la contraseña, cerrar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Destructive delete dialog. Requires the admin to type "BORRAR" to enable
 * the confirm button — cheap defence against fat-fingering.
 */
function DeleteUserDialog({ open, onOpenChange, user, onDeleted }) {
    const [confirmText, setConfirmText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) setConfirmText(""); }, [open]);

    const canConfirm = confirmText.trim().toUpperCase() === "BORRAR";

    const onSubmit = async () => {
        if (!canConfirm) return;
        setSubmitting(true);
        try {
            await api.delete(`/users/${user.id}`);
            toast.success(`Usuario ${user.name} eliminado`);
            onDeleted?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo borrar");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="delete-user-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                            <Trash size={18} weight="bold" />
                        </div>
                        <div>
                            <DialogTitle>Borrar usuario</DialogTitle>
                            <DialogDescription>
                                Vas a eliminar a <strong>{user?.name}</strong> (<code className="text-xs">{user?.email}</code>).
                                Sus conversaciones asignadas quedarán sin asignar.
                                <br/>Esta acción no se puede deshacer.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-1.5">
                    <Label>Escribe <code className="bg-slate-100 px-1 py-0.5 rounded">BORRAR</code> para confirmar</Label>
                    <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="BORRAR"
                        data-testid="delete-user-confirm-input"
                        autoFocus
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="delete-user-cancel">Cancelar</Button>
                    <Button
                        onClick={onSubmit}
                        disabled={!canConfirm || submitting}
                        variant="destructive"
                        data-testid="delete-user-confirm"
                    >
                        {submitting ? "Borrando…" : "Borrar definitivamente"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UsersPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [generatedPayload, setGeneratedPayload] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch {
            toast.error("No se pudo cargar la lista de usuarios");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Count active admins so we can gray-out destructive actions on the last one.
    const activeAdminCount = users.filter(u => u.role === "admin" && u.active).length;
    const isLastAdmin = (u) => u.role === "admin" && u.active && activeAdminCount <= 1;

    return (
        <div data-testid="admin-users-section">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Usuarios</h1>
                    <div className="text-sm text-slate-500">
                        Gestión del equipo de Viaja de Verdad. Los usuarios marcados <Badge variant="secondary" className="mx-0.5 text-[9px] uppercase">Seed</Badge> son datos semilla; los reales no lo llevan.
                    </div>
                </div>
                <Button onClick={() => setCreateOpen(true)} className="bg-brand-600 hover:bg-brand-700"
                        data-testid="users-new-btn">
                    <Plus size={16} className="mr-1.5"/> Nuevo usuario
                </Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <Table data-testid="users-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Usuario</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Rol</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead className="text-right w-[260px]">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Cargando…</TableCell></TableRow>
                        )}
                        {!loading && users.length === 0 && (
                            <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Sin usuarios.</TableCell></TableRow>
                        )}
                        {users.map(u => {
                            const isSelf = u.id === currentUser?.id;
                            const lastAdmin = isLastAdmin(u);
                            const deleteBlocked = isSelf || lastAdmin;
                            return (
                                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                    <TableCell>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Avatar name={u.name} color={u.avatar_color} size={28}/>
                                            <span className={`text-sm ${u.active ? "text-slate-800" : "text-slate-400 line-through"}`}>
                                                {u.name}
                                            </span>
                                            {isSelf && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-brand-700 bg-brand-100 border border-brand-200 px-1.5 py-0.5 rounded"
                                                      data-testid={`user-badge-self-${u.id}`}>
                                                    Tú
                                                </span>
                                            )}
                                            {u.is_seed && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded"
                                                      data-testid={`user-badge-seed-${u.id}`}>
                                                    Seed
                                                </span>
                                            )}
                                            {u.force_password_reset && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded"
                                                      data-testid={`user-badge-forcereset-${u.id}`}
                                                      title="El usuario tiene una contraseña temporal y deberá cambiarla al iniciar sesión.">
                                                    Reset pendiente
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="text-[10px] uppercase tracking-wider font-bold">
                                            {ROLE_LABEL[u.role]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {u.active
                                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Activo</span>
                                            : <span className="inline-flex items-center gap-1 text-xs text-slate-500"><span className="w-1.5 h-1.5 rounded-full bg-slate-400"/>Inactivo</span>}
                                    </TableCell>
                                    <TableCell className="text-right whitespace-nowrap">
                                        <Button variant="ghost" size="sm" onClick={() => setEditTarget(u)}
                                                data-testid={`user-edit-${u.id}`}>
                                            <Pencil size={14} className="mr-1"/>Editar
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)}
                                                data-testid={`user-reset-${u.id}`}>
                                            <KeyReturn size={14} className="mr-1"/>Password
                                        </Button>
                                        <Button
                                            variant="ghost" size="sm"
                                            onClick={() => setDeleteTarget(u)}
                                            disabled={deleteBlocked}
                                            title={
                                                isSelf ? "No puedes borrarte a ti mismo/a."
                                                    : lastAdmin ? "Es el último admin activo. Añade otro admin antes de borrarlo."
                                                    : "Borrar usuario"
                                            }
                                            className={deleteBlocked ? "opacity-40 cursor-not-allowed" : "text-red-600 hover:text-red-700 hover:bg-red-50"}
                                            data-testid={`user-delete-${u.id}`}
                                        >
                                            <Trash size={14} className="mr-1"/>Borrar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            <UserDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                mode="create"
                onCreated={(payload) => { setGeneratedPayload(payload); load(); }}
            />
            <UserDialog
                open={!!editTarget}
                onOpenChange={(o) => !o && setEditTarget(null)}
                mode="edit"
                initial={editTarget}
                onSaved={load}
            />
            <ResetPasswordDialog
                open={!!resetTarget}
                onOpenChange={(o) => !o && setResetTarget(null)}
                user={resetTarget}
                onGenerated={(payload) => { setGeneratedPayload(payload); load(); }}
            />
            <DeleteUserDialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
                user={deleteTarget}
                onDeleted={load}
            />
            <GeneratedPasswordDialog
                payload={generatedPayload}
                onClose={() => setGeneratedPayload(null)}
            />
        </div>
    );
}

// ============================ CHANNELS ============================

// ============================ CHANNEL HEALTH (Phase 4.4) ============================

/**
 * Compact health card for the /admin channels page. Polls
 * GET /api/admin/channels/status every 15s and renders three rows:
 *   - WS (KimKim WebSocket adapter)
 *   - IMAP (KimKim IMAP poller — belt-and-suspenders)
 *   - Cookies (Playwright refresher scheduler)
 * plus a top amber banner if any alert_* boolean is true.
 *
 * The card is self-contained (own fetch loop, own interval), so it can be
 * embedded in any admin sub-route without prop drilling.
 */
function _healthDotColour(state) {
    switch (state) {
        case "connected":
        case "healthy":
        case "ok":
            return "bg-emerald-500";
        case "reconnecting":
        case "degraded":
        case "retrying":
            return "bg-amber-500";
        case "disconnected":
        case "failed":
            return "bg-red-500";
        case "disabled":
        case "unknown":
        default:
            return "bg-slate-300";
    }
}

function _timeAgo(iso) {
    if (!iso) return "nunca";
    try {
        const now = Date.now();
        const then = new Date(iso).getTime();
        const secs = Math.max(0, Math.floor((now - then) / 1000));
        if (secs < 60) return `hace ${secs}s`;
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `hace ${mins}min`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `hace ${hours}h`;
        return `hace ${Math.floor(hours / 24)}d`;
    } catch { return "?"; }
}

function _timeUntil(iso) {
    if (!iso) return "—";
    try {
        const secs = Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
        if (secs === 0) return "ya";
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `en ${mins}min`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `en ${hours}h`;
        return `en ${Math.floor(hours / 24)}d`;
    } catch { return "?"; }
}

function _humanWsLabel(ws) {
    if (!ws.enabled) return "Deshabilitado (feature flag off)";
    if (ws.state === "connected") return `Conectado ${_timeAgo(ws.connected_since)}`;
    if (ws.state === "reconnecting") return `Reconectando (intento ${ws.reconnect_attempts || 0})`;
    if (ws.state === "disconnected") return `Caído ${_timeAgo(ws.last_disconnect_at)}`;
    return ws.state;
}
function _humanImapLabel(imap) {
    if (!imap.enabled) return "Deshabilitado";
    if (imap.state === "healthy") return `Último poll ${_timeAgo(imap.last_poll_at)}`;
    if (imap.state === "degraded") return `Degradado — poll obsoleto ${_timeAgo(imap.last_poll_at)}`;
    if (imap.state === "failed") return `Falló (${imap.consecutive_errors} errores consecutivos)`;
    return imap.state;
}
function _humanCookiesLabel(cookies) {
    const last = cookies.last_refresh_at;
    if (cookies.refresh_status === "failed") {
        return `Refresh falló — reintentando · último OK ${_timeAgo(last)}`;
    }
    if (cookies.refresh_status === "retrying") {
        return `Reintentando refresh · último OK ${_timeAgo(last)}`;
    }
    return `Refrescadas ${_timeAgo(last)} · próximo ${_timeUntil(cookies.next_refresh_at)}`;
}

function ChannelHealthCard() {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [diagOpen, setDiagOpen] = useState(false);
    const [diagLoading, setDiagLoading] = useState(false);
    const [diagData, setDiagData] = useState(null);
    const [diagError, setDiagError] = useState(null);
    const [diagStartedAt, setDiagStartedAt] = useState(null);

    useEffect(() => {
        let stopped = false;
        const fetchHealth = async () => {
            try {
                const { data } = await api.get("/admin/channels/status");
                if (!stopped) setHealth(data.health || null);
            } catch { /* leave stale */ }
            finally { if (!stopped) setLoading(false); }
        };
        fetchHealth();
        const id = setInterval(fetchHealth, 15000);
        return () => { stopped = true; clearInterval(id); };
    }, []);

    const runDiagnose = async () => {
        // Guard: si ya hay una llamada en vuelo, no lanzamos otra. El
        // endpoint diagnose ejecuta un login HTTP real (~2-5 s) y admite
        // rate-limit propio en backend, pero evitar la doble-llamada en
        // cliente es la barrera más barata contra el doble-click.
        if (diagLoading) return;
        setDiagOpen(true);
        setDiagLoading(true);
        setDiagData(null);
        setDiagError(null);
        setDiagStartedAt(new Date());
        try {
            const { data } = await api.get("/admin/kimkim/diagnose-refresh");
            setDiagData(data);
        } catch (e) {
            setDiagError(
                formatApiErrorDetail(e.response?.data?.detail)
                || e.message
                || "No se pudo ejecutar el diagnóstico"
            );
        } finally {
            setDiagLoading(false);
        }
    };

    const copyDiagnose = async () => {
        if (!diagData) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(diagData, null, 2));
            toast.success("JSON copiado al portapapeles");
        } catch {
            toast.error("No se pudo copiar (¿portapapeles bloqueado?)");
        }
    };

    if (loading) {
        return (
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 text-sm text-slate-500"
                 data-testid="channel-health-card">
                Cargando estado de canales…
            </div>
        );
    }
    if (!health) return null;

    const { ws, imap, cookies, alerts } = health;
    const anyAlert = alerts && (alerts.alert_ws_down || alerts.alert_imap_stale || alerts.alert_cookies_stale);
    const showDiagnoseBtn = alerts && (alerts.alert_ws_down || alerts.alert_cookies_stale);

    const alertMessages = [];
    if (alerts?.alert_ws_down) alertMessages.push("WebSocket de KimKim caído. Los mensajes siguen entrando por IMAP con delay de 30-60s.");
    if (alerts?.alert_imap_stale) alertMessages.push("Poller IMAP obsoleto (>10min sin poll). Verifica logs del backend.");
    if (alerts?.alert_cookies_stale) alertMessages.push("Refresh de cookies fallando o cookies >30h antiguas. WS puede caer en breve.");

    return (
        <div className="mb-4" data-testid="channel-health-card">
            {anyAlert && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-900"
                     data-testid="channel-health-alert-banner">
                    <Warning size={18} weight="fill" className="shrink-0 text-amber-600 mt-0.5"/>
                    <div className="flex-1">
                        <div className="font-semibold mb-0.5">Atención — canales KimKim en estado inusual</div>
                        <ul className="list-disc list-inside space-y-0.5 text-xs">
                            {alertMessages.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                    </div>
                    {showDiagnoseBtn && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="shrink-0 border-amber-400 text-amber-900 hover:bg-amber-100"
                            onClick={runDiagnose}
                            data-testid="channel-health-diagnose-btn"
                        >
                            <ArrowsClockwise size={14} className="mr-1.5" />
                            Diagnosticar
                        </Button>
                    )}
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Estado de canales</h3>
                    <span className="text-[10px] text-slate-400">auto-refresh 15s</span>
                </div>

                <div className="divide-y divide-slate-100">
                    <div className="flex items-center gap-3 py-2" data-testid="health-row-ws">
                        <span className={`w-2.5 h-2.5 rounded-full ${_healthDotColour(ws.state)}`}
                              data-testid="health-ws-dot" />
                        <Broadcast size={16} className="text-slate-500"/>
                        <span className="text-sm font-medium text-slate-800 w-24">WebSocket</span>
                        <span className="text-sm text-slate-600" data-testid="health-ws-label">
                            {_humanWsLabel(ws)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 py-2" data-testid="health-row-imap">
                        <span className={`w-2.5 h-2.5 rounded-full ${_healthDotColour(imap.state)}`}
                              data-testid="health-imap-dot" />
                        <EnvelopeOpen size={16} className="text-slate-500"/>
                        <span className="text-sm font-medium text-slate-800 w-24">IMAP poller</span>
                        <span className="text-sm text-slate-600" data-testid="health-imap-label">
                            {_humanImapLabel(imap)}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 py-2" data-testid="health-row-cookies">
                        <span className={`w-2.5 h-2.5 rounded-full ${_healthDotColour(cookies.refresh_status)}`}
                              data-testid="health-cookies-dot" />
                        <Cookie size={16} className="text-slate-500"/>
                        <span className="text-sm font-medium text-slate-800 w-24">Cookies</span>
                        <span className="text-sm text-slate-600" data-testid="health-cookies-label">
                            {_humanCookiesLabel(cookies)}
                        </span>
                    </div>
                </div>
            </div>

            <ChannelDiagnoseModal
                open={diagOpen}
                onOpenChange={setDiagOpen}
                loading={diagLoading}
                data={diagData}
                error={diagError}
                startedAt={diagStartedAt}
                onRerun={runDiagnose}
                onCopy={copyDiagnose}
            />
        </div>
    );
}


function ChannelDiagnoseModal({
    open, onOpenChange, loading, data, error, startedAt, onRerun, onCopy,
}) {
    const summary = data
        ? {
            strategy: data.strategy_active || "unknown",
            httpOutcome: data.http_login?.outcome || "—",
            httpGet: data.http_login?.get_status,
            httpPost: data.http_login?.post_status,
            csrfFound: data.http_login?.csrf_found,
            elapsedMs: data.http_login?.elapsed_ms,
            loginOutcome: data.login_attempt?.outcome,
            cookieCount: data.login_attempt?.cookie_count,
            sessionUpdated: data.login_attempt?.session_updated,
        }
        : null;
    const isHealthy = summary && summary.loginOutcome === "ok";
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[85vh] flex flex-col"
                data-testid="channel-diagnose-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowsClockwise size={18} />
                        Diagnóstico de refresh de cookies KimKim
                    </DialogTitle>
                    <DialogDescription>
                        Ejecuta un login HTTP real contra KimKim y devuelve el resultado paso a paso.
                        Sin secretos: la contraseña nunca sale del pod, las cookies se listan solo por nombre.
                        {startedAt && (
                            <> Iniciado {startedAt.toLocaleTimeString("es-ES")}.</>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-auto space-y-3" data-testid="channel-diagnose-body">
                    {loading && (
                        <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500"
                             data-testid="channel-diagnose-loading">
                            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                            Ejecutando login… (hasta ~90s)
                        </div>
                    )}
                    {!loading && error && (
                        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
                             data-testid="channel-diagnose-error">
                            <div className="font-semibold mb-1">Endpoint falló</div>
                            <div className="text-xs whitespace-pre-wrap break-all">{error}</div>
                        </div>
                    )}
                    {!loading && summary && (
                        <>
                            <div className={`rounded-md border p-3 text-sm ${
                                isHealthy
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                                    : "border-rose-300 bg-rose-50 text-rose-900"
                            }`} data-testid="channel-diagnose-summary">
                                <div className="font-semibold mb-1">
                                    {isHealthy ? "Login OK" : "Login falló"}
                                    <span className="ml-2 text-xs opacity-70">strategy = {summary.strategy}</span>
                                </div>
                                <ul className="text-xs grid grid-cols-2 gap-x-4 gap-y-0.5">
                                    <li>GET status: <b>{summary.httpGet ?? "—"}</b></li>
                                    <li>POST status: <b>{summary.httpPost ?? "—"}</b></li>
                                    <li>CSRF found: <b>{summary.csrfFound === true ? "sí" : summary.csrfFound === false ? "no" : "—"}</b></li>
                                    <li>Elapsed: <b>{summary.elapsedMs != null ? `${summary.elapsedMs}ms` : "—"}</b></li>
                                    <li>Login outcome: <b>{summary.loginOutcome || "—"}</b></li>
                                    <li>Cookies recibidas: <b>{summary.cookieCount ?? "—"}</b></li>
                                    <li>Session actualizada: <b>{summary.sessionUpdated ? "sí" : "no"}</b></li>
                                </ul>
                                {data.login_attempt?.error && (
                                    <div className="mt-2 text-xs">
                                        <b>Error:</b> {data.login_attempt.error}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                    JSON completo
                                </div>
                                <pre className="bg-slate-900 text-slate-100 text-[11px] leading-relaxed rounded-md p-3 overflow-x-auto max-h-[45vh]"
                                     data-testid="channel-diagnose-json">
{JSON.stringify(data, null, 2)}
                                </pre>
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={loading || !data}
                        onClick={onCopy}
                        data-testid="channel-diagnose-copy-btn"
                    >
                        <Copy size={14} className="mr-1.5" />
                        Copiar JSON
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        onClick={onRerun}
                        data-testid="channel-diagnose-rerun-btn"
                    >
                        <ArrowsClockwise size={14} className="mr-1.5" />
                        Reintentar
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        data-testid="channel-diagnose-close-btn"
                    >
                        Cerrar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ============================ CHANNELS PAGE ============================

function ChannelsPage() {
    const [data, setData] = useState(null);
    const [outbound, setOutbound] = useState(null);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);
    const [pollResult, setPollResult] = useState(null);
    const [replaying, setReplaying] = useState(false);
    const [replayResult, setReplayResult] = useState(null);
    const fileInputRef = useRef(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/channels/status").then(r => r.data).catch(() => null),
            api.get("/admin/outbound/status").then(r => r.data).catch(() => null),
        ]).then(([d, ob]) => {
            setData(d); setOutbound(ob);
        }).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const togglePause = async () => {
        try {
            const path = outbound?.paused ? "/admin/outbound/resume" : "/admin/outbound/pause";
            await api.post(path);
            toast.success(outbound?.paused ? "Envíos a clientes reanudados" : "Envíos a clientes pausados (kill-switch ON)");
            load();
        } catch (e) {
            toast.error("No se pudo cambiar el estado");
        }
    };

    const forcePoll = async () => {
        setPolling(true);
        try {
            const { data } = await api.post("/admin/channels/kimkim/poll");
            setPollResult(data);
            if (data.error) {
                toast.error("Poll falló: " + data.error);
            } else if (data.baseline_set) {
                toast.success(`Baseline establecido en UID ${data.last_uid_after}. El siguiente poll capturará nuevos.`);
            } else if ((data.processed || 0) > 0) {
                toast.success(`Poll OK · ${data.processed} mensajes nuevos procesados`);
            } else {
                toast.success(`Poll OK · ${data.fetched || 0} fetched, sin nuevos`);
            }
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Poll falló");
        } finally {
            setPolling(false);
        }
    };

    const resetBaseline = async () => {
        if (!window.confirm("¿Re-establecer baseline? El próximo poll snapshoteará el UID actual e ignorará todo el histórico previo.")) return;
        try {
            await api.post("/admin/channels/kimkim/reset-baseline");
            toast.success("Baseline reseteado. Lanza un poll para fijar el nuevo UID base.");
            load();
        } catch (e) {
            toast.error("No se pudo resetear");
        }
    };

    const onPickFile = () => fileInputRef.current?.click();

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";  // allow re-selecting same file
        if (!f) return;
        setReplaying(true);
        setReplayResult(null);
        try {
            const fd = new FormData();
            fd.append("eml", f, f.name);
            const { data } = await api.post("/admin/channels/kimkim/replay-eml", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReplayResult(data);
            const r = data.result;
            if (r.status === "parsed") {
                if (r.is_new_conversation) {
                    toast.success(`✉️ Email procesado · conversación creada (${r.conversation_id.slice(0,8)}…)`);
                } else {
                    toast.success(`✉️ Email procesado · añadido a conversación existente`);
                }
            } else if (r.status === "duplicate") {
                toast.message("Email ya estaba en el sistema (dedup por Message-ID).");
            } else {
                toast.error("Parser detectó email incompleto: " + (r.warnings || []).join(", "));
            }
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fallo al procesar .eml");
        } finally {
            setReplaying(false);
        }
    };

    if (loading || !data) {
        return <div data-testid="admin-channels-section" className="text-slate-400">Cargando estado de canales…</div>;
    }

    const kimkim = data.channels.find(c => c.name === "kimkim") || {};
    const manual = data.channels.find(c => c.name === "manual") || {};
    const travefy = data.channels.find(c => c.name === "travefy") || {};

    return (
        <div data-testid="admin-channels-section" className="space-y-6">
            <div>
                <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Canales</h1>
                <p className="text-sm text-slate-500">
                    Estado en vivo de los adapters de mensajería. El poller IMAP de KimKim corre cada{" "}
                    <code>{kimkim.poll_interval_seconds || 30}s</code> cuando está habilitado en el .env.
                </p>
            </div>

            {/* Phase 4.4 — live health card with auto-refresh + alert banner */}
            <ChannelHealthCard />

            {/* ====== OUTBOUND CONTROL PANEL ====== */}
            <div className={"rounded-lg p-4 border " + (outbound?.paused
                ? "bg-red-50 border-red-300"
                : (outbound?.smtp_enabled ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"))}
                 data-testid="outbound-control-panel">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-slate-900">Envíos outbound (SMTP)</span>
                            {outbound?.paused
                                ? <Badge variant="destructive" data-testid="outbound-paused-badge">⏸ PAUSADO</Badge>
                                : (outbound?.smtp_enabled
                                    ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="outbound-active-badge">▶ ACTIVO</Badge>
                                    : <Badge variant="secondary">SMTP off</Badge>)}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                            Host: <code>{outbound?.smtp_host || "—"}</code> · From: <code>{outbound?.smtp_from || "—"}</code>
                            {" · "}Whitelist outbound: <code>{(outbound?.outbound_whitelist || []).join(", ") || "(ninguna)"}</code>
                            {" · "}Rate: {outbound?.rate_limit_per_min || 0}/min·agente
                        </p>
                    </div>
                    <Button onClick={togglePause} variant={outbound?.paused ? "default" : "destructive"}
                            disabled={!outbound}
                            className={outbound?.paused ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            data-testid="outbound-kill-switch-btn">
                        {outbound?.paused ? "Reanudar envíos a clientes" : "Pausar envíos a clientes (kill-switch)"}
                    </Button>
                </div>
                {outbound?.paused && (
                    <div className="mt-3 text-xs p-2.5 bg-red-100 border border-red-200 rounded text-red-900"
                         data-testid="outbound-paused-banner">
                        ⚠️ Envíos a clientes bloqueados. Todos los intentos de responder devolverán 503 hasta que reanudes.
                    </div>
                )}
            </div>

            {/* ====== KIMKIM ====== */}
            <div className="bg-white border border-slate-200 rounded-lg p-5"
                 data-testid="channel-card-kimkim">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-lg text-slate-900">KimKim</span>
                            {kimkim.state?.healthy
                                ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="kimkim-healthy-badge">Healthy</Badge>
                                : <Badge variant="destructive" data-testid="kimkim-degraded-badge">Degraded</Badge>}
                            {kimkim.poller_enabled
                                ? <Badge className="bg-blue-100 text-blue-700">Poller ON</Badge>
                                : <Badge variant="secondary">Poller OFF</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            Transporte: <code>{kimkim.transport}</code> · intervalo:{" "}
                            <code>{kimkim.poll_interval_seconds}s</code>
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                        <Button onClick={onPickFile} disabled={replaying} variant="outline"
                                data-testid="kimkim-replay-eml-btn">
                            {replaying ? "Procesando…" : "📥 Subir .eml de prueba"}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".eml,message/rfc822,text/plain"
                            onChange={onFileChosen}
                            className="hidden"
                            data-testid="kimkim-replay-eml-input"
                        />
                        <Button onClick={forcePoll} disabled={polling}
                                className="bg-brand-600 hover:bg-brand-700"
                                data-testid="kimkim-force-poll-btn">
                            {polling ? "Polling…" : "Forzar poll ahora"}
                        </Button>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <Metric label="Último UID" value={kimkim.state?.last_uid ?? "—"}/>
                    <Metric label="Último poll" value={kimkim.state?.last_poll_at ? formatDate(kimkim.state.last_poll_at, "d MMM HH:mm:ss") : "—"}/>
                    <Metric label="Procesados (total)" value={kimkim.state?.messages_processed ?? 0}/>
                    <Metric label="Duplicados (skip)" value={kimkim.state?.duplicates_skipped ?? 0}/>
                    <Metric label="Errores seguidos" value={kimkim.state?.consecutive_errors ?? 0}/>
                    <Metric label="Último éxito" value={kimkim.state?.last_success_at ? formatDate(kimkim.state.last_success_at, "d MMM HH:mm:ss") : "—"}/>
                    <Metric label="Último error" value={kimkim.state?.last_error || "—"} narrow/>
                    <Metric label="Whitelist trips" value={(kimkim.trip_whitelist || []).join(", ") || "(sin whitelist)"} narrow/>
                </div>

                {pollResult && (
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded text-xs font-mono whitespace-pre-wrap"
                         data-testid="kimkim-last-poll-result">
                        {JSON.stringify(pollResult, null, 2)}
                    </div>
                )}

                {replayResult && (
                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs font-mono whitespace-pre-wrap"
                         data-testid="kimkim-last-replay-result">
                        <div className="font-sans text-[11px] text-emerald-700 uppercase tracking-wide mb-1">Replay .eml — {replayResult.filename} ({replayResult.bytes} bytes)</div>
                        {JSON.stringify(replayResult.result, null, 2)}
                    </div>
                )}

                {!kimkim.poller_enabled && (
                    <div className="mt-3 text-xs p-2.5 bg-amber-50 border border-amber-200 rounded text-amber-800">
                        El polling automático está OFF. Para activarlo, en <code>backend/.env</code>{" "}
                        ajusta <code>KIMKIM_POLLER_ENABLED=true</code> y reinicia el backend.
                        Mientras tanto, «Forzar poll ahora» funciona manualmente y «Subir .eml» permite
                        validar el parser end-to-end sin tocar IMAP.
                    </div>
                )}

                <div className="mt-3 text-right">
                    <button onClick={resetBaseline}
                            className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                            data-testid="kimkim-reset-baseline-btn">
                        Re-establecer baseline (avanzado)
                    </button>
                </div>
            </div>

            {/* ====== MANUAL ====== */}
            <div className="bg-white border border-slate-200 rounded-lg p-5"
                 data-testid="channel-card-manual">
                <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-slate-900">Manual (simulado)</div>
                    <Badge className="bg-emerald-100 text-emerald-700">Healthy</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                    Canal de pruebas. Recibe inbound vía <code>POST /api/channels/manual/inbound</code>.
                    Útil para sembrar conversaciones o reproducir escenarios.
                </p>
            </div>

            {/* ====== TRAVEFY ====== */}
            <div className="bg-white border border-slate-200 rounded-lg p-5"
                 data-testid="channel-card-travefy">
                <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-slate-900">Travefy</div>
                    <Badge variant="secondary">Inactivo</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">
                    Pendiente para Fase 2+. Stub registrado en <code>adapters.py</code>.
                </p>
            </div>
        </div>
    );
}

function Metric({ label, value, narrow = false }) {
    return (
        <div className={narrow ? "col-span-2 md:col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-sm font-medium text-slate-800 break-all">{String(value)}</div>
        </div>
    );
}

// ============================ EMAIL LOG ============================

function EmailLogPage() {
    const [tab, setTab] = useState("inbound");  // inbound | outbound
    const [outboundLogs, setOutboundLogs] = useState([]);
    const [inboundLogs, setInboundLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [detail, setDetail] = useState(null);  // currently inspected inbound row

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/email-log").then(r => r.data).catch(() => []),
            api.get("/admin/inbound-email-log").then(r => r.data).catch(() => []),
        ]).then(([out, inb]) => {
            setOutboundLogs(out);
            setInboundLogs(inb);
        }).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const sendTest = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/admin/email-log/test");
            toast.success(data.smtp_enabled ? "Email enviado." : "Email stubeado (SMTP desactivado).");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falló el test");
        } finally {
            setSending(false);
        }
    };

    const openDetail = async (row) => {
        try {
            const { data } = await api.get(`/admin/inbound-email-log/${row._id}`);
            setDetail(data);
        } catch (e) {
            toast.error("No se pudo cargar el detalle");
        }
    };

    return (
        <div data-testid="admin-emaillog-section">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Log de mensajes</h1>
                    <p className="text-sm text-slate-500">
                        <strong>Recibidos</strong>: mensajes del chat de KimKim parseados por el poller IMAP.
                        {" "}<strong>Enviados</strong>: mensajes que el Hub ha enviado a los chats de KimKim (o
                        stubbed si <code>SMTP_ENABLED=false</code>).
                    </p>
                </div>
                <Button onClick={sendTest} disabled={sending} variant="outline" data-testid="emaillog-test-btn">
                    {sending ? "Mandando…" : "Test SMTP a mí"}
                </Button>
            </div>

            <div className="flex gap-1 mb-3" data-testid="emaillog-tabs">
                <button
                    onClick={() => setTab("inbound")}
                    data-testid="emaillog-tab-inbound"
                    className={"px-3 py-1.5 text-sm font-medium rounded-md transition-colors " +
                        (tab === "inbound" ? "bg-brand-100 text-brand-700" : "text-slate-500 hover:bg-slate-100")}>
                    Recibidos ({inboundLogs.length})
                </button>
                <button
                    onClick={() => setTab("outbound")}
                    data-testid="emaillog-tab-outbound"
                    className={"px-3 py-1.5 text-sm font-medium rounded-md transition-colors " +
                        (tab === "outbound" ? "bg-brand-100 text-brand-700" : "text-slate-500 hover:bg-slate-100")}>
                    Enviados ({outboundLogs.length})
                </button>
            </div>

            {tab === "inbound" && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <Table data-testid="emaillog-inbound-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Fecha</TableHead>
                                <TableHead>Remitente</TableHead>
                                <TableHead>Cliente (subject)</TableHead>
                                <TableHead>Trip slug</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="w-[200px]">Message-ID</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Cargando…</TableCell></TableRow>}
                            {!loading && inboundLogs.length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">
                                    Aún no hay emails inbound procesados. Cuando KimKim mande un mensaje, aparecerá aquí.
                                </TableCell></TableRow>
                            )}
                            {inboundLogs.map(l => (
                                <TableRow key={l._id}
                                          data-testid={`inbound-row-${l._id}`}
                                          onClick={() => openDetail(l)}
                                          className="cursor-pointer hover:bg-slate-50">
                                    <TableCell className="text-xs text-slate-500">{formatDate(l.created_at, "d MMM HH:mm")}</TableCell>
                                    <TableCell className="text-sm">{l.from_email}</TableCell>
                                    <TableCell className="text-sm">{l.customer_name}</TableCell>
                                    <TableCell className="text-xs"><code>{l.trip_slug || "—"}</code></TableCell>
                                    <TableCell>
                                        {l.status === "parsed"    && <Badge className="bg-emerald-100 text-emerald-700">PARSED</Badge>}
                                        {l.status === "duplicate" && <Badge variant="secondary">DUPLICATE</Badge>}
                                        {l.status === "error"     && <Badge variant="destructive">ERROR</Badge>}
                                        {l.status === "filtered"  && <Badge variant="secondary">FILTERED</Badge>}
                                    </TableCell>
                                    <TableCell className="text-[10px] font-mono text-slate-400 truncate max-w-[200px]">
                                        {l.message_id}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {tab === "outbound" && (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <Table data-testid="emaillog-outbound-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Fecha</TableHead>
                                <TableHead>Destinatario</TableHead>
                                <TableHead>Asunto</TableHead>
                                <TableHead>Estado</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Cargando…</TableCell></TableRow>}
                            {!loading && outboundLogs.length === 0 && (
                                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">
                                    Aún no hay emails outbound en el log.
                                </TableCell></TableRow>
                            )}
                            {outboundLogs.map(l => (
                                <TableRow key={l._id} data-testid={`emaillog-row-${l._id}`}>
                                    <TableCell className="text-xs text-slate-500">{formatDate(l.created_at, "d MMM HH:mm")}</TableCell>
                                    <TableCell className="text-sm">{l.to_email}</TableCell>
                                    <TableCell className="text-sm">{l.subject}</TableCell>
                                    <TableCell>
                                        {l.status === "stubbed" && <Badge variant="secondary">STUBBED</Badge>}
                                        {l.status === "sent"    && <Badge className="bg-emerald-100 text-emerald-700">SENT</Badge>}
                                        {l.status === "queued"  && <Badge className="bg-blue-100 text-blue-700">QUEUED</Badge>}
                                        {l.status === "failed"  && <Badge variant="destructive">FAILED</Badge>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl" data-testid="inbound-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>Detalle del email inbound</DialogTitle>
                        <DialogDescription className="text-xs">
                            Message-ID: <code>{detail?.message_id}</code>
                        </DialogDescription>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-3 text-sm max-h-[60vh] overflow-auto">
                            <Metric label="From" value={detail.from_email}/>
                            <Metric label="Reply-To" value={detail.reply_to || "—"}/>
                            <Metric label="Subject" value={detail.subject}/>
                            <Metric label="Customer" value={detail.customer_name}/>
                            <Metric label="Trip slug" value={detail.trip_slug || "—"}/>
                            <Metric label="Conversation" value={detail.conversation_id || "—"}/>
                            <Metric label="EML on disk" value={detail.eml_path || "—"} narrow/>
                            <Metric label="Warnings" value={(detail.warnings || []).join(", ") || "—"} narrow/>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Body parsed (preview)</div>
                                <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-xs whitespace-pre-wrap">
{detail.body_preview}
                                </pre>
                            </div>
                            {detail.eml_raw && (
                                <details>
                                    <summary className="cursor-pointer text-xs text-slate-600">Ver .eml crudo</summary>
                                    <pre className="bg-slate-900 text-slate-200 rounded p-2 text-[10px] mt-2 whitespace-pre-wrap max-h-80 overflow-auto">
{detail.eml_raw}
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ============================ SIMULATE INBOUND ============================

function SimulateInboundPage() {
    const [form, setForm] = useState({
        trip_external_id: "manual_" + Math.random().toString(36).slice(2, 8),
        trip_title: "",
        trip_destination: "",
        customer_name: "",
        customer_email: "",
        body: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const onSubmit = async () => {
        if (!form.customer_name.trim() || !form.body.trim()) {
            toast.error("Cliente y mensaje son obligatorios");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                trip_external_id: form.trip_external_id,
                trip_title: form.trip_title || null,
                trip_destination: form.trip_destination || null,
                customer_name: form.customer_name,
                customer_email: form.customer_email || null,
                body: form.body,
                channel: "manual",
            };
            const { data } = await api.post("/channels/manual/inbound", payload);
            setLastResult(data);
            toast.success("Mensaje simulado entregado al inbox");
            setForm(f => ({
                ...f,
                trip_external_id: "manual_" + Math.random().toString(36).slice(2, 8),
                body: "",
            }));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al simular");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-simulate-section" className="max-w-2xl">
            <h1 className="font-display text-2xl font-bold text-slate-900">Simular mensaje inbound</h1>
            <p className="text-sm text-slate-500 mb-4">
                Crea un mensaje entrante del canal <code>manual</code> tal y como vendría de KimKim/Travefy.
                Útil para probar el inbox antes de tener las integraciones reales.
            </p>

            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label>Trip external id</Label>
                        <Input value={form.trip_external_id}
                               onChange={(e) => setForm(f => ({ ...f, trip_external_id: e.target.value }))}
                               data-testid="sim-trip-id"/>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Título del viaje (opcional)</Label>
                        <Input placeholder="Roma & Toscana en familia"
                               value={form.trip_title}
                               onChange={(e) => setForm(f => ({ ...f, trip_title: e.target.value }))}
                               data-testid="sim-trip-title"/>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>Destino (opcional)</Label>
                    <Input placeholder="Italia"
                           value={form.trip_destination}
                           onChange={(e) => setForm(f => ({ ...f, trip_destination: e.target.value }))}
                           data-testid="sim-trip-destination"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label>Nombre del cliente</Label>
                        <Input value={form.customer_name}
                               onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                               data-testid="sim-customer-name"/>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Email del cliente (opcional)</Label>
                        <Input type="email" value={form.customer_email}
                               onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
                               data-testid="sim-customer-email"/>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label>Texto del mensaje</Label>
                    <Textarea rows={5}
                              placeholder="Hola, llegamos mañana, ¿podéis confirmarme el horario?"
                              value={form.body}
                              onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                              data-testid="sim-body"/>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Canal: <Badge variant="secondary">manual</Badge></span>
                    <Button onClick={onSubmit} disabled={submitting} className="bg-brand-600 hover:bg-brand-700"
                            data-testid="sim-submit">
                        <PaperPlaneTilt size={14} className="mr-1.5"/>
                        {submitting ? "Enviando…" : "Simular entrada"}
                    </Button>
                </div>
            </div>

            {lastResult && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-800"
                     data-testid="sim-last-result">
                    <div className="font-bold mb-1">Último entregado</div>
                    <div>conversation_id: <code>{lastResult.conversation_id}</code></div>
                    <div>message_id: <code>{lastResult.message_id}</code></div>
                    <div className="mt-1">Vete al Inbox y verás la conversación arriba (los compañeros la verán en tiempo real por WebSocket).</div>
                </div>
            )}
        </div>
    );
}

// ============================ Duplicate trips (Phase 6) ==============================

function DuplicateTripsPage() {
    // Panel defensivo: lista los slugs de KimKim con >1 trip en Mongo.
    // Con el dual-lookup del persist_ws_message (Phase 6) esto debería
    // quedar vacío en régimen normal. Aparece cuando algo se rompió
    // upstream — botón "Combinar" hace merge idempotente.
    const [state, setState] = useState({ loading: true, clusters: [], error: null });
    const [mergeTarget, setMergeTarget] = useState(null);   // {slug, masterConv, dupConv}
    const [mergePreview, setMergePreview] = useState(null); // dry-run result
    const [mergeSubmitting, setMergeSubmitting] = useState(false);

    const reload = useCallback(async () => {
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const { data } = await api.get("/admin/duplicate-trips");
            setState({ loading: false, clusters: data.clusters || [], error: null });
        } catch (e) {
            setState({
                loading: false,
                clusters: [],
                error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
            });
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const openMerge = async (slug, masterConv, dupConv) => {
        setMergeTarget({ slug, masterConv, dupConv });
        setMergePreview(null);
        try {
            const { data } = await api.post("/admin/conversations/merge", {
                master_conv_id: masterConv.conv_id,
                duplicate_conv_id: dupConv.conv_id,
                dry_run: true,
            });
            setMergePreview(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error en dry-run");
            setMergePreview({ error: e.response?.data?.detail || e.message });
        }
    };

    const confirmMerge = async () => {
        if (!mergeTarget) return;
        setMergeSubmitting(true);
        try {
            const { data } = await api.post("/admin/conversations/merge", {
                master_conv_id: mergeTarget.masterConv.conv_id,
                duplicate_conv_id: mergeTarget.dupConv.conv_id,
                dry_run: false,
            });
            toast.success(
                `Merge completado: ${data.messages_to_move} mensajes movidos`
            );
            setMergeTarget(null);
            setMergePreview(null);
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "El merge falló");
        } finally {
            setMergeSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-duplicate-trips-section" className="max-w-5xl">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800">Conversaciones duplicadas potenciales</h2>
                    <p className="text-xs text-slate-500 max-w-2xl">
                        Trips de KimKim que existen más de una vez en Mongo (mismo slug, docs distintos).
                        Con el fix de <code>persist_ws_message</code> esto debería estar vacío. Si aparece
                        algo, revisa los logs del ingest — probablemente un bug upstream — y usa &quot;Combinar&quot;
                        para consolidar sin perder mensajes.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={reload} data-testid="dup-refresh-btn">
                    <ArrowsClockwise size={14} className="mr-1.5" />
                    Recargar
                </Button>
            </div>

            {state.error && (
                <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 p-3 text-sm mb-3"
                     data-testid="dup-error">
                    {state.error}
                </div>
            )}

            {state.loading && (
                <div className="text-sm text-slate-500 py-8 text-center"
                     data-testid="dup-loading">
                    Cargando…
                </div>
            )}

            {!state.loading && state.clusters.length === 0 && !state.error && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-6 text-center"
                     data-testid="dup-empty">
                    <CheckCircle size={32} weight="fill" className="text-emerald-600 inline-block mb-2" />
                    <div className="text-sm font-medium text-emerald-900">Sin duplicados</div>
                    <div className="text-xs text-emerald-700 mt-1">
                        Todos los trips de KimKim tienen exactamente un documento en Mongo.
                    </div>
                </div>
            )}

            {!state.loading && state.clusters.map(cluster => {
                const convs = cluster.convs || [];
                if (convs.length < 2) return null;
                const sorted = [...convs].sort((a, b) => {
                    const at = new Date(a.trip_created_at || 0).getTime();
                    const bt = new Date(b.trip_created_at || 0).getTime();
                    return at - bt;
                });
                const master = sorted[0];
                const dups = sorted.slice(1);
                return (
                    <div key={cluster.slug} className="mb-4 bg-white border border-slate-200 rounded-lg overflow-hidden"
                         data-testid={`dup-cluster-${cluster.slug}`}>
                        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                            <div className="text-sm">
                                <span className="text-slate-500">Slug:</span>{" "}
                                <code className="font-mono font-medium text-slate-900">{cluster.slug}</code>
                                <span className="text-xs text-slate-500 ml-2">
                                    ({cluster.n_trips} trips)
                                </span>
                            </div>
                        </div>
                        <div className="p-3">
                            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Master (más antiguo)</div>
                            <ConvRow c={master} isMaster />
                            <div className="text-xs font-semibold text-rose-700 uppercase tracking-wider mt-3 mb-1">Duplicados</div>
                            {dups.map(d => (
                                <div key={d.conv_id} className="flex items-start gap-2">
                                    <div className="flex-1"><ConvRow c={d} /></div>
                                    <Button size="sm" variant="outline"
                                            onClick={() => openMerge(cluster.slug, master, d)}
                                            data-testid={`dup-merge-btn-${d.conv_id}`}
                                            className="mt-2 border-brand-400 text-brand-700 hover:bg-brand-50">
                                        <Copy size={12} className="mr-1.5" />
                                        Combinar
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}

            <Dialog open={!!mergeTarget} onOpenChange={(v) => !v && setMergeTarget(null)}>
                <DialogContent className="max-w-2xl" data-testid="dup-merge-modal">
                    <DialogHeader>
                        <DialogTitle>Combinar conversaciones</DialogTitle>
                        <DialogDescription>
                            Los mensajes del duplicado se mueven al master y luego se elimina el duplicado.
                            Operación irreversible sin restaurar desde <code>/app/backups/</code>.
                        </DialogDescription>
                    </DialogHeader>
                    {mergeTarget && !mergePreview && (
                        <div className="text-sm text-slate-500 py-4 text-center">Calculando previsualización…</div>
                    )}
                    {mergePreview && mergePreview.error && (
                        <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 p-3 text-sm">
                            {String(mergePreview.error)}
                        </div>
                    )}
                    {mergePreview && !mergePreview.error && (
                        <div className="space-y-2 text-sm" data-testid="dup-merge-preview">
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                    <div><b>Master</b>: <code>{mergeTarget.masterConv.conv_id.slice(0, 8)}</code></div>
                                    <div><b>Duplicate</b>: <code>{mergeTarget.dupConv.conv_id.slice(0, 8)}</code></div>
                                    <div>Mensajes a mover: <b>{mergePreview.messages_to_move}</b></div>
                                    <div>Dedup por external_id: <b>{mergePreview.messages_deduped_via_external_id}</b></div>
                                    <div>Notas a mover: <b>{mergePreview.notes_to_move}</b></div>
                                    <div>Unread sumado: <b>+{mergePreview.unread_added}</b></div>
                                    <div>Trip duplicado eliminado: <b>{mergePreview.duplicate_trip_will_be_deleted ? "sí" : "no"}</b></div>
                                    <div>Customer duplicado eliminado: <b>{mergePreview.duplicate_customer_will_be_deleted ? "sí" : "no"}</b></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMergeTarget(null)}
                                disabled={mergeSubmitting}
                                data-testid="dup-merge-cancel">
                            Cancelar
                        </Button>
                        <Button onClick={confirmMerge}
                                disabled={!mergePreview || mergePreview.error || mergeSubmitting}
                                data-testid="dup-merge-confirm"
                                className="bg-brand-600 hover:bg-brand-700 text-white">
                            {mergeSubmitting ? "Ejecutando…" : "Confirmar merge"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ConvRow({ c, isMaster = false }) {
    return (
        <div className={`rounded-md border p-2 my-1 flex items-center gap-3 text-xs ${
            isMaster
                ? "border-emerald-300 bg-emerald-50"
                : "border-rose-200 bg-white"
        }`}>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                    {c.trip_title || "—"}
                </div>
                <div className="text-slate-500 truncate">
                    conv <code>{c.conv_id.slice(0, 8)}</code> · trip <code>{c.trip_id.slice(0, 8)}</code>
                    {" · customer "}<code>{(c.customer_id || "n/a").slice(0, 8)}</code>
                </div>
            </div>
            <div className="text-right shrink-0">
                <div>{c.n_messages} msgs</div>
                <div className="text-slate-400">{c.trip_created_at ? formatDate(c.trip_created_at) : ""}</div>
            </div>
        </div>
    );
}


// ============================ First-touches (Phase 5) ==============================

function FirstTouchesPage() {
    // Panel admin de "primeras conversaciones" — muestra cada vez que un
    // usuario del Hub escribió por primera vez a un trip de KimKim en los
    // últimos N días. Útil para monitorear el rollout de mode=all_kimkim.
    const [items, setItems] = useState(null);   // null = loading
    const [days, setDays] = useState(7);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setItems(null);
        setError(null);
        api.get(`/admin/first-touches?days=${days}&limit=200`)
            .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
            .catch(e => {
                if (!cancelled) {
                    setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                    setItems([]);
                }
            });
        return () => { cancelled = true; };
    }, [days]);

    return (
        <div data-testid="admin-first-touches-section" className="max-w-5xl">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800">Primeras conversaciones</h2>
                    <p className="text-xs text-slate-500">
                        Cada vez que un miembro del equipo escribe por primera vez a un trip.
                        Útil para monitorear el rollout del modo <code>all_kimkim</code>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">Ventana</label>
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="text-sm border border-slate-300 rounded-md px-2 py-1"
                        data-testid="first-touches-days-select"
                    >
                        <option value={1}>Últimas 24h</option>
                        <option value={7}>Últimos 7 días</option>
                        <option value={30}>Últimos 30 días</option>
                        <option value={90}>Últimos 90 días</option>
                    </select>
                </div>
            </div>

            {error && (
                <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 p-3 text-sm mb-3"
                     data-testid="first-touches-error">
                    {error}
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <Table data-testid="first-touches-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Agente</TableHead>
                            <TableHead>Trip</TableHead>
                            <TableHead>Cliente</TableHead>
                            <TableHead className="w-[180px]">Timestamp</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items === null && (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                                    Cargando…
                                </TableCell>
                            </TableRow>
                        )}
                        {items && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center text-slate-400 py-8"
                                           data-testid="first-touches-empty">
                                    Ninguna primera conversación en el rango seleccionado.
                                </TableCell>
                            </TableRow>
                        )}
                        {items && items.map(it => (
                            <TableRow key={it.id} data-testid={`first-touch-row-${it.id}`}>
                                <TableCell>
                                    <div className="text-sm font-medium text-slate-800">{it.user_name}</div>
                                    <div className="text-xs text-slate-500">{it.user_email}</div>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm text-slate-800 truncate max-w-xs" title={it.trip_title}>
                                        {it.trip_title}
                                    </div>
                                    {it.trip_slug && (
                                        <code className="text-[10px] text-slate-500">{it.trip_slug}</code>
                                    )}
                                </TableCell>
                                <TableCell className="text-sm text-slate-700">{it.customer_name}</TableCell>
                                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                                    {formatDate(it.first_message_sent_at)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}


// ============================ RELIABILITY (Phase 15) ============================

const KIND_LABEL = {
    gap_detected:              "Gap detectado",
    gap_recovered:             "Gap recuperado",
    gap_scan_completed:        "Gap scan completado",
    ws_heartbeat_ok:           "WS heartbeat OK",
    ws_stalled:                "WS stalled",
    ws_slug_broken:            "WS slug broken",
    ws_session_refreshed:      "WS sesión reconectada",
    push_audit_ok:             "Push OK",
    push_audit_missing:        "Push falta",
    push_zombie_purged:        "Push sub purgada",
    autoheal_action:           "Auto-heal",
    autoheal_skipped_flag_off: "Auto-heal saltado (flag off)",
    autoheal_circuit_breaker:  "Circuit breaker",
    autoheal_rate_limited:     "Rate limit",
    flag_toggled:              "Flag toggled",
    // Phase 15.3
    email_fallback_sent:            "Email fallback enviado",
    email_fallback_skipped:         "Email fallback saltado",
    slug_broken_retry_success:      "Slug recuperado (retry)",
    slug_broken_retry_failed:       "Slug retry falló",
    slug_marked_permanently_broken: "Slug marcado permanent",
    // Phase 15.4
    client_message_alert_sent:      "Alerta buzón enviada",
    client_message_alert_skipped:   "Alerta buzón saltada",
    message_broadcast_no_recipients:"Msg sin destinatarios (⚠)",
    // Phase 15.4b — Gap recovered alert
    gap_recovered_alert_sent:       "Alerta recovery enviada",
    gap_recovered_alert_skipped:    "Alerta recovery saltada",
    // Phase 15.5 — Wrapper backfill (KimKim notification-only emails)
    wrapper_backfill_run:           "Wrapper backfill OK",
    wrapper_backfill_failed:        "Wrapper backfill falló",
};
const SEV_STYLE = {
    info:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn:  "bg-amber-50 text-amber-700 border-amber-200",
    error: "bg-rose-50 text-rose-700 border-rose-200",
};

function ReliabilityTile({ title, statusLabel, statusTone = "info", children, testId }) {
    // `tone` maps to the SEV_STYLE palette so all four tiles feel consistent.
    return (
        <div
            data-testid={testId}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col gap-3 min-h-[168px]"
        >
            <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-800 tracking-tight">{title}</h3>
                {statusLabel && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${SEV_STYLE[statusTone]}`}>
                        {statusLabel}
                    </span>
                )}
            </div>
            <div className="text-sm text-slate-600 flex-1">{children}</div>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex justify-between gap-3 text-xs">
            <span className="text-slate-500 shrink-0">{k}</span>
            <span className="text-slate-800 font-medium truncate">{v ?? "—"}</span>
        </div>
    );
}

function formatSince(iso) {
    if (!iso) return "nunca";
    try {
        const t = new Date(iso).getTime();
        const s = Math.max(0, Math.round((Date.now() - t) / 1000));
        if (s < 60) return `hace ${s}s`;
        if (s < 3600) return `hace ${Math.round(s / 60)}min`;
        if (s < 86400) return `hace ${Math.round(s / 3600)}h`;
        return `hace ${Math.round(s / 86400)}d`;
    } catch { return "—"; }
}

function ReliabilityPage() {
    const [state, setState] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [filterKind, setFilterKind] = useState("all");
    const [syncJob, setSyncJob] = useState(null);        // último job (o el running)
    const [syncing, setSyncing] = useState(false);       // botón
    // Phase 15.5 — Wrapper repair job (equivalent of sync but for wrappers)
    const [wrapperJob, setWrapperJob] = useState(null);
    const [wrapperFixing, setWrapperFixing] = useState(false);
    const [wrapperConfirmOpen, setWrapperConfirmOpen] = useState(false);
    // Phase 15.4 — Diagnostic modal
    const [diagOpen, setDiagOpen] = useState(false);
    const [diagWindow, setDiagWindow] = useState("7d");
    const [diagData, setDiagData] = useState(null);
    const [diagLoading, setDiagLoading] = useState(false);
    const [diagError, setDiagError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [s, e, sj, wj] = await Promise.all([
                api.get("/admin/reliability/state"),
                api.get("/admin/reliability/recent-actions", { params: { limit: 100 } }),
                api.get("/admin/reliability/sync-from-kimkim", { params: { limit: 1 } }),
                api.get("/admin/reliability/fix-wrappers", { params: { limit: 1 } }),
            ]);
            setState(s.data);
            setEvents(e.data?.items || []);
            setSyncJob((sj.data?.items || [])[0] || null);
            setWrapperJob((wj.data?.items || [])[0] || null);
            setError(null);
        } catch (err) {
            setError(formatApiErrorDetail(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        // Faster polling while a sync or wrapper repair is running.
        const running = syncJob?.status === "running" || wrapperJob?.status === "running";
        const id = setInterval(refresh, running ? 4000 : 15000);
        return () => clearInterval(id);
    }, [refresh, syncJob?.status, wrapperJob?.status]);

    const toggleAutoheal = async () => {
        if (!state) return;
        const next = !state.flags?.autoheal;
        // Nice safety: confirm the destructive direction (turning it OFF).
        if (!next && !window.confirm("¿Apagar AUTOHEAL para todo el sistema?")) return;
        setToggling(true);
        try {
            await api.post("/admin/reliability/autoheal/toggle", { enabled: next });
            toast.success(`AUTOHEAL ${next ? "activado" : "apagado"}`);
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err));
        } finally {
            setToggling(false);
        }
    };

    const startSync = async () => {
        // Manual "Sincronizar desde KimKim" — scans active convs (30d) and
        // backfills the missing messages. Idempotent, safe to click twice.
        if (!window.confirm(
            "¿Iniciar sincronización desde KimKim?\n\n"
            + "Escanea todas las conversaciones activas (30 días) y recupera "
            + "los mensajes que falten. Tarda ~3-5 min. Es seguro repetirlo."
        )) return;
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/reliability/sync-from-kimkim", { days: 30 });
            if (data.status === "already_running") {
                toast.info("Ya hay una sincronización en curso.");
            } else {
                toast.success("Sincronización iniciada.");
            }
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err));
        } finally {
            setSyncing(false);
        }
    };

    // Phase 15.5 — "Reparar mensajes wrapper" — one-click backfill of
    // KimKim notification-only emails whose real body lives on
    // kimkim.com. Runs the same code as `python -m
    // scripts.backfill_message_bodies --apply --include-flagged` but
    // async in-process so Eduardo doesn't need shell access.
    //
    // Phase 15.5.1 (fix P0 stale emails, 28-sep-2026): the wrapper
    // repair NO LONGER dispatches client_alert for messages older than
    // CLIENT_ALERT_MAX_AGE_MINUTES (default 60). Retroactive runs are
    // silent as far as the shared inbox is concerned; only wrappers
    // from the last hour (i.e. mensajes que acaban de llegar en tiempo
    // real) generan email. El result del job trae `emails_suppressed_stale`
    // con el conteo de alertas evitadas.
    const startFixWrappers = async () => {
        setWrapperConfirmOpen(false);
        setWrapperFixing(true);
        try {
            const { data } = await api.post("/admin/reliability/fix-wrappers");
            if (data.status === "already_running") {
                toast.info("Ya hay una reparación en curso.");
            } else {
                toast.success("Reparación de wrappers iniciada.");
            }
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err));
        } finally {
            setWrapperFixing(false);
        }
    };

    if (loading) return <div className="p-6 text-sm text-slate-500">Cargando…</div>;
    if (error) return (
        <div className="p-6" data-testid="reliability-error">
            <div className="text-sm text-rose-600">Error: {error}</div>
        </div>
    );
    if (!state) return null;

    const ws = state.ws || {};
    const gaps = state.gaps || {};
    const push = state.push || {};
    const imap = state.imap || {};
    const counters = state.counters || {};
    const brokenCount = Object.keys(ws.broken_slugs || {}).length;

    const wsTone = ws.connected ? "info" : "warn";
    const wsStatus = ws.connected ? "conectado" : (ws.enabled ? "reconectando" : "off");
    const gapsTone = (gaps.active_gaps_24h ?? 0) > 0 ? "warn" : "info";
    const gapsStatus = (gaps.active_gaps_24h ?? 0) > 0 ? `${gaps.active_gaps_24h} convs` : "sin gaps";
    const pushTone = (push.users_missing_subs ?? 0) > 0 ? "warn" : "info";
    const pushStatus = (push.users_missing_subs ?? 0) > 0 ? `${push.users_missing_subs} sin sub` : "OK";
    const imapEnabled = imap.enabled !== false;   // treat undefined (legacy) as enabled
    const imapTone = !imapEnabled ? "warn" : (imap.error ? "error" : "info");
    const imapStatus = !imapEnabled
        ? "apagado"
        : (imap.last_poll_at ? formatSince(imap.last_poll_at) : "sin datos");

    const autoheal = state.autoheal || {};
    const cbs = autoheal.circuit_breakers || [];
    const rl = autoheal.rate_limit || {};

    // Phase 15.3 — new tiles & hints
    const emailFb = state.email_fallback || {};
    const mttr = state.mttr || {};
    const mttr24 = mttr["24h"] || {};
    const mttr7d = mttr["7d"] || {};
    const permanentlyBroken = state.permanently_broken_slugs || {};
    const configHints = state.config_hints || {};
    const wsHint = configHints.ws_disabled;
    const emailTone = emailFb.enabled ? "info" : "warn";
    const emailStatus = emailFb.enabled
        ? `${emailFb.sent_24h ?? 0} enviados 24h`
        : "off";
    const mttrTone = (mttr24.count ?? 0) > 0 ? "info" : "warn";
    const mttrStatus = (mttr24.count ?? 0) > 0
        ? `${mttr24.count} recoveries`
        : "sin datos";

    // Phase 15.4 — Client alerts tile (email al buzón compartido)
    const clientAlert = state.client_alert || {};
    const clientAlertTone = clientAlert.enabled ? "info" : "warn";
    const clientAlertStatus = clientAlert.enabled
        ? `${clientAlert.sent_24h ?? 0} enviados 24h`
        : "off";
    // Phase 15.4b — Gap recovered alerts tile (email a los agentes)
    const gapAlert = state.gap_alert || {};
    const gapAlertTone = gapAlert.enabled ? "info" : "warn";
    const gapAlertStatus = gapAlert.enabled
        ? `${gapAlert.sent_24h ?? 0} enviados 24h`
        : "off";

    const permanentSlugsList = Object.entries(permanentlyBroken || {})
        .filter(([_, v]) => typeof v === "object")
        .sort(([_a, a], [_b, b]) => (b.marked_at || "").localeCompare(a.marked_at || ""));

    const filtered = filterKind === "all" ? events : events.filter(e => e.kind === filterKind);
    const kindOptions = Array.from(new Set(events.map(e => e.kind))).sort();

    const resetCircuitBreaker = async (conv_id) => {
        if (!window.confirm("¿Reactivar auto-heal para esta conversación ahora?")) return;
        try {
            await api.post(`/admin/reliability/circuit-breakers/${conv_id}/reset`);
            toast.success("Circuit breaker desbloqueado");
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err));
        }
    };

    const retryPermanentSlug = async (slug) => {
        if (!window.confirm(
            `¿Reintentar el slug ${slug}?\n\n` +
            "Si el retry falla, seguirá marcado como permanently_broken."
        )) return;
        try {
            const { data } = await api.post(
                `/admin/reliability/permanently-broken-slugs/${encodeURIComponent(slug)}/retry`
            );
            if (data.ok) {
                toast.success(`Slug ${slug} recuperado`);
            } else {
                toast.error(`Retry falló: ${data.error || "unknown"}`);
            }
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err));
        }
    };

    const fmtSecs = (s) => {
        if (s == null) return "—";
        if (s < 60) return `${Math.round(s)}s`;
        if (s < 3600) return `${(s / 60).toFixed(1)}min`;
        return `${(s / 3600).toFixed(1)}h`;
    };

    const openDiagnostic = async (win) => {
        setDiagOpen(true);
        setDiagLoading(true);
        setDiagError(null);
        setDiagData(null);
        const w = win || diagWindow;
        try {
            const { data } = await api.get("/admin/reliability/diagnostic", { params: { window: w } });
            setDiagData(data);
        } catch (err) {
            setDiagError(formatApiErrorDetail(err));
        } finally {
            setDiagLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6" data-testid="reliability-root">
            {/* Header + flag control */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold text-slate-900 tracking-tight">Reliability</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Detección proactiva de gaps, salud del WS/IMAP y estado de push. Datos refrescan cada 15 s.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <div className="text-xs text-slate-500">
                        Layer <span className={state.layer_enabled ? "text-emerald-700 font-semibold" : "text-slate-400"}>
                            {state.layer_enabled ? "ENABLED" : "OFF"}
                        </span>
                    </div>
                    <Button
                        onClick={() => openDiagnostic("7d")}
                        variant="outline"
                        data-testid="reliability-diagnostic-open"
                        className="whitespace-nowrap"
                    >
                        Ver diagnóstico
                    </Button>
                    <Button
                        onClick={startSync}
                        disabled={syncing || syncJob?.status === "running"}
                        variant="outline"
                        data-testid="reliability-sync-start"
                        className="whitespace-nowrap"
                    >
                        {syncJob?.status === "running" ? "Sincronizando…" : "Sincronizar desde KimKim"}
                    </Button>
                    <Button
                        onClick={() => setWrapperConfirmOpen(true)}
                        disabled={wrapperFixing || wrapperJob?.status === "running"}
                        variant="outline"
                        data-testid="reliability-fix-wrappers-start"
                        className="whitespace-nowrap"
                    >
                        {wrapperJob?.status === "running" ? "Reparando wrappers…" : "Reparar mensajes wrapper"}
                    </Button>
                    <Button
                        onClick={toggleAutoheal}
                        disabled={toggling}
                        variant={state.flags?.autoheal ? "default" : "outline"}
                        data-testid="reliability-autoheal-toggle"
                        className="whitespace-nowrap"
                    >
                        AUTOHEAL: {state.flags?.autoheal ? "ON" : "OFF"}
                    </Button>
                </div>
            </div>

            {/* Sync job card (visible when there is a recent sync) */}            {syncJob && (
                <div
                    data-testid="reliability-sync-card"
                    className={
                        "rounded-2xl border p-4 shadow-sm " +
                        (syncJob.status === "running"
                            ? "border-blue-200 bg-blue-50"
                            : syncJob.status === "completed"
                                ? "border-emerald-200 bg-emerald-50"
                                : syncJob.status === "failed"
                                    ? "border-rose-200 bg-rose-50"
                                    : "border-amber-200 bg-amber-50")
                    }
                >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800">
                                Sincronización desde KimKim ·{" "}
                                <span className="uppercase text-xs tracking-wide">
                                    {syncJob.status}
                                </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1">
                                Iniciada {formatSince(syncJob.started_at)}
                                {syncJob.finished_at && ` · terminó ${formatSince(syncJob.finished_at)}`}
                            </div>
                            {syncJob.status === "running" && syncJob.progress?.total > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs text-slate-600 mb-1">
                                        {syncJob.progress.current}/{syncJob.progress.total} trips ·{" "}
                                        {syncJob.progress.current_slug || "…"}
                                    </div>
                                    <div className="h-2 bg-white/60 rounded overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 transition-all"
                                            style={{ width: `${Math.round((syncJob.progress.current / Math.max(1, syncJob.progress.total)) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                            {syncJob.status === "completed" && syncJob.result && (
                                <div className="mt-2 text-xs text-slate-700">
                                    {syncJob.result.cleanup && syncJob.result.cleanup.deleted > 0 && (
                                        <div className="mb-1">
                                            <b>Limpieza de duplicados:</b> {syncJob.result.cleanup.deleted} borrados en {syncJob.result.cleanup.convs_affected} convs
                                        </div>
                                    )}
                                    Escaneadas <b>{syncJob.result.total_scanned}</b> · con gaps <b>{syncJob.result.with_gaps}</b> · <b>{syncJob.result.total_inserted}</b> mensajes recuperados · errores <b>{syncJob.result.errors?.length || 0}</b>
                                    {syncJob.result.rob_cohen && (
                                        <div className="mt-1">
                                            Rob Cohen: <b>{syncJob.result.rob_cohen.total_messages}</b> msgs · P0 presente: <b>{syncJob.result.rob_cohen.p0_present ? "sí" : "no"}</b>
                                        </div>
                                    )}
                                </div>
                            )}
                            {syncJob.error && (
                                <div className="mt-2 text-xs text-rose-700">Error: {syncJob.error}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Wrapper repair job card (Phase 15.5) — visible when there is a recent wrapper repair.
                Muestra progreso + resultado incluyendo `emails_suppressed_stale` (Phase 15.5.1)
                para que se vea de un vistazo cuántos wrappers históricos NO dispararon email. */}
            {wrapperJob && (
                <div
                    data-testid="reliability-wrapper-card"
                    className={
                        "rounded-2xl border p-4 shadow-sm " +
                        (wrapperJob.status === "running"
                            ? "border-blue-200 bg-blue-50"
                            : wrapperJob.status === "completed"
                                ? "border-emerald-200 bg-emerald-50"
                                : wrapperJob.status === "failed"
                                    ? "border-rose-200 bg-rose-50"
                                    : "border-amber-200 bg-amber-50")
                    }
                >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800">
                                Reparación de mensajes wrapper ·{" "}
                                <span className="uppercase text-xs tracking-wide">
                                    {wrapperJob.status}
                                </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1">
                                Iniciada {formatSince(wrapperJob.started_at)}
                                {wrapperJob.finished_at && ` · terminó ${formatSince(wrapperJob.finished_at)}`}
                            </div>
                            {wrapperJob.status === "running" && wrapperJob.progress?.total > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs text-slate-600 mb-1">
                                        {wrapperJob.progress.current}/{wrapperJob.progress.total} trips ·{" "}
                                        {wrapperJob.progress.current_slug || "…"}
                                    </div>
                                    <div className="h-2 bg-white/60 rounded overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 transition-all"
                                            style={{ width: `${Math.round((wrapperJob.progress.current / Math.max(1, wrapperJob.progress.total)) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                            {(wrapperJob.status === "completed" || wrapperJob.status === "running") && wrapperJob.result && (
                                <div className="mt-2 text-xs text-slate-700 space-y-1" data-testid="reliability-wrapper-result">
                                    <div>
                                        Wrappers detectados <b>{wrapperJob.result.wrappers_found ?? 0}</b>
                                        {" · "}reemplazados <b>{wrapperJob.result.replaced_ok ?? 0}</b>
                                        {" · "}duplicados eliminados <b>{wrapperJob.result.duplicates_removed ?? 0}</b>
                                        {" · "}errores <b>{wrapperJob.result.errors_list?.length ?? 0}</b>
                                    </div>
                                    <div data-testid="reliability-wrapper-suppressed-stale">
                                        Emails suprimidos por antigüedad{" "}
                                        <span className="text-slate-500">(&gt; 60 min)</span>:{" "}
                                        <b>{wrapperJob.result.emails_suppressed_stale ?? 0}</b>
                                    </div>
                                </div>
                            )}
                            {wrapperJob.error && (
                                <div className="mt-2 text-xs text-rose-700">Error: {wrapperJob.error}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 6 tiles (Phase 15.3: + MTTR + Email fallback) */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <ReliabilityTile
                    title="WebSocket · KimKim"
                    statusLabel={wsStatus}
                    statusTone={wsTone}
                    testId="tile-ws"
                >
                    <div className="space-y-1.5">
                        {wsHint && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2" data-testid="tile-ws-hint">
                                {wsHint}
                            </div>
                        )}
                        <KV k="Último frame" v={formatSince(ws.last_frame_at)} />
                        <KV k="Canales suscritos" v={(ws.subscribed_channels || []).length} />
                        <KV k="Desconexiones" v={ws.disconnects_total} />
                        <KV k="Slugs broken (30min)" v={brokenCount} />
                        <KV k="Notif. recibidas" v={ws.notifications_received_total} />
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="Gap scanner"
                    statusLabel={gapsStatus}
                    statusTone={gapsTone}
                    testId="tile-gaps"
                >
                    <div className="space-y-1.5">
                        <KV k="Último scan" v={formatSince(gaps.last_scan_at)} />
                        <KV k="Último scan profundo" v={formatSince(gaps.last_deep_scan_at)} />
                        <KV k="Convs con gaps (24 h)" v={gaps.active_gaps_24h ?? 0} />
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="Push notifications"
                    statusLabel={pushStatus}
                    statusTone={pushTone}
                    testId="tile-push"
                >
                    <div className="space-y-1.5">
                        <KV k="Subs totales" v={push.total_subscriptions} />
                        <KV k="Usuarios con sub" v={push.users_with_subs} />
                        <KV k="Activos 7 d" v={push.active_users_7d} />
                        <KV k="Sin sub" v={push.users_missing_subs} />
                        <KV k="Último audit" v={formatSince(push.last_audit_at)} />
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="IMAP poller"
                    statusLabel={imapStatus}
                    statusTone={imapTone}
                    testId="tile-imap"
                >
                    <div className="space-y-1.5">
                        {!imapEnabled ? (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                {imap.disabled_hint || "Poller apagado. Setea la variable KIMKIM_POLLER_ENABLED=true en el deployment para activarlo."}
                            </div>
                        ) : (
                            <>
                                <KV k="Último poll" v={formatSince(imap.last_poll_at)} />
                                <KV k="Último éxito" v={formatSince(imap.last_success_at)} />
                                <KV k="Msgs procesados" v={imap.messages_processed ?? "—"} />
                                <KV k="Errores seguidos" v={imap.consecutive_errors ?? 0} />
                                <KV k="Error último" v={imap.error || "ninguno"} />
                            </>
                        )}
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="MTTR (Mean Time To Recovery)"
                    statusLabel={mttrStatus}
                    statusTone={mttrTone}
                    testId="tile-mttr"
                >
                    <div className="space-y-1.5">
                        <div className="text-[11px] text-slate-500">Ventana 24h</div>
                        <KV k="Recoveries" v={mttr24.count ?? 0} />
                        <KV k="Media" v={fmtSecs(mttr24.mean_seconds)} />
                        <KV k="p50" v={fmtSecs(mttr24.p50_seconds)} />
                        <KV k="p95" v={fmtSecs(mttr24.p95_seconds)} />
                        <div className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                            7d: <b>{mttr7d.count ?? 0}</b> recoveries · media {fmtSecs(mttr7d.mean_seconds)}
                        </div>
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="Email fallback"
                    statusLabel={emailStatus}
                    statusTone={emailTone}
                    testId="tile-email-fallback"
                >
                    <div className="space-y-1.5">
                        {!emailFb.enabled && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2" data-testid="tile-email-fallback-hint">
                                {emailFb.disabled_hint || "Email fallback apagado. Setea EMAIL_FALLBACK_ENABLED=true."}
                            </div>
                        )}
                        {emailFb.enabled && (
                            <>
                                <KV k="Cooldown" v={`${emailFb.cooldown_minutes ?? 15} min`} />
                                <KV k="Enviados 24h" v={emailFb.sent_24h ?? 0} />
                                <KV k="Enviados 7d" v={emailFb.sent_7d ?? 0} />
                                {Array.isArray(emailFb.exclusion_list) && emailFb.exclusion_list.length > 0 && (
                                    <div
                                        className="text-[11px] text-slate-500 mt-1"
                                        data-testid="tile-email-fallback-exclusions"
                                    >
                                        Excluidos: {emailFb.exclusion_list.join(", ")} ({emailFb.exclusion_list.length})
                                    </div>
                                )}
                                <div className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                                    Se dispara cuando el push no llega. Rate-limited 1 email cada {emailFb.cooldown_minutes ?? 15} min por (agente, conv).
                                </div>
                            </>
                        )}
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="Alertas buzón compartido"
                    statusLabel={clientAlertStatus}
                    statusTone={clientAlertTone}
                    testId="tile-client-alert"
                >
                    <div className="space-y-1.5">
                        {!clientAlert.enabled ? (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="tile-client-alert-hint">
                                {clientAlert.disabled_hint || "Alertas apagadas. Setea CLIENT_MESSAGE_ALERT_ENABLED=true."}
                            </div>
                        ) : (
                            <>
                                <KV
                                    k={Array.isArray(clientAlert.recipients) && clientAlert.recipients.length > 1 ? "Destinatarios" : "Destinatario"}
                                    v={
                                        Array.isArray(clientAlert.recipients) && clientAlert.recipients.length > 0
                                            ? clientAlert.recipients.join(", ")
                                            : (clientAlert.recipient || "—")
                                    }
                                />
                                {Array.isArray(clientAlert.excluded_recipients) && clientAlert.excluded_recipients.length > 0 && (
                                    <div
                                        className="text-[11px] text-slate-500"
                                        data-testid="tile-client-alert-exclusions"
                                    >
                                        Excluidos: {clientAlert.excluded_recipients.join(", ")} ({clientAlert.excluded_recipients.length})
                                    </div>
                                )}
                                <KV k="Última hora" v={clientAlert.sent_1h ?? 0} />
                                <KV k="Enviadas 24h" v={clientAlert.sent_24h ?? 0} />
                                <KV k="Enviadas 7d" v={clientAlert.sent_7d ?? 0} />
                                <div className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                                    Se envía al buzón compartido por CADA mensaje de cliente. Independiente del push.
                                </div>
                            </>
                        )}
                    </div>
                </ReliabilityTile>

                <ReliabilityTile
                    title="Alertas recovery"
                    statusLabel={gapAlertStatus}
                    statusTone={gapAlertTone}
                    testId="tile-gap-alert"
                >
                    <div className="space-y-1.5">
                        {!gapAlert.enabled ? (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="tile-gap-alert-hint">
                                {gapAlert.disabled_hint || "Alertas de recovery apagadas. Setea GAP_RECOVERED_ALERT_ENABLED=true."}
                            </div>
                        ) : (
                            <>
                                <KV k="Roles" v={Array.isArray(gapAlert.target_roles) ? gapAlert.target_roles.join(", ") : "—"} />
                                {Array.isArray(gapAlert.extra_recipients) && gapAlert.extra_recipients.length > 0 && (
                                    <KV k="Extras" v={gapAlert.extra_recipients.join(", ")} />
                                )}
                                {Array.isArray(gapAlert.excluded_recipients) && gapAlert.excluded_recipients.length > 0 && (
                                    <div
                                        className="text-[11px] text-slate-500"
                                        data-testid="tile-gap-alert-exclusions"
                                    >
                                        Excluidos: {gapAlert.excluded_recipients.join(", ")} ({gapAlert.excluded_recipients.length})
                                    </div>
                                )}
                                <KV k="Cooldown" v={`${gapAlert.cooldown_minutes ?? 5} min`} />
                                <KV k="Enviadas 24h" v={gapAlert.sent_24h ?? 0} />
                                <KV k="Enviadas 7d" v={gapAlert.sent_7d ?? 0} />
                                <div className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                                    Se envía cuando el auto-heal recupera un gap. Cascada a todos los admin/support/guardia + extras.
                                </div>
                            </>
                        )}
                    </div>
                </ReliabilityTile>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-2xl font-semibold text-slate-900">{counters.events_24h ?? 0}</div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">Eventos 24 h</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-2xl font-semibold text-slate-900">{counters.actions_24h ?? 0}</div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">Acciones auto-heal 24 h</div>
                </div>
                <div className={`rounded-xl border p-4 ${counters.errors_24h ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}>
                    <div className={`text-2xl font-semibold ${counters.errors_24h ? "text-rose-700" : "text-slate-900"}`}>
                        {counters.errors_24h ?? 0}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">Errores 24 h</div>
                </div>
            </div>

            {/* Layer 2 — Auto-heal (rate limit + circuit breakers) */}
            <div
                className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                data-testid="reliability-autoheal-section"
            >
                <div className="flex items-baseline justify-between gap-3 mb-3">
                    <h3 className="text-sm font-semibold text-slate-800">Auto-heal (Layer 2)</h3>
                    <div className="text-xs text-slate-500">
                        {state.flags?.autoheal ? "activo · corriendo" : "apagado por flag"}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Rate limit (última hora)</div>
                        <div className="text-lg font-semibold text-slate-900 mt-1">
                            {rl.actions_last_hour ?? 0}
                            <span className="text-sm text-slate-400 font-normal"> / {rl.capacity ?? 0}</span>
                        </div>
                        {rl.capacity > 0 && (
                            <div className="h-2 bg-slate-100 rounded overflow-hidden mt-2">
                                <div
                                    className={
                                        "h-full transition-all "
                                        + ((rl.actions_last_hour ?? 0) >= rl.capacity ? "bg-rose-500" : "bg-emerald-500")
                                    }
                                    style={{ width: `${Math.min(100, Math.round((rl.actions_last_hour ?? 0) / Math.max(1, rl.capacity) * 100))}%` }}
                                />
                            </div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-1">
                            Tokens disponibles ahora: <b>{rl.tokens_available ?? 0}</b>
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Circuit breakers activos</div>
                        {cbs.length === 0 ? (
                            <div className="text-sm text-slate-500 mt-2" data-testid="reliability-cbs-empty">
                                Ninguno.
                            </div>
                        ) : (
                            <div className="space-y-2 mt-2" data-testid="reliability-cbs-list">
                                {cbs.map((cb) => (
                                    <div
                                        key={cb.conv_id}
                                        className="flex items-center justify-between gap-3 p-2 rounded border border-amber-200 bg-amber-50 text-xs"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium text-amber-900 truncate">
                                                {cb.trip_title || cb.conv_id.slice(0, 8)}
                                            </div>
                                            <div className="text-[10px] text-amber-700 truncate">
                                                {cb.customer_name ? cb.customer_name + " · " : ""}
                                                se libera en {Math.round((cb.remaining_seconds || 0) / 3600)}h
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-[11px]"
                                            data-testid={`reliability-cb-reset-${cb.conv_id.slice(0,8)}`}
                                            onClick={() => resetCircuitBreaker(cb.conv_id)}
                                        >
                                            Reactivar
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Timeline */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="reliability-timeline">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800">Timeline de eventos</h3>
                    <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500">Filtro</Label>
                        <Select value={filterKind} onValueChange={setFilterKind}>
                            <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="reliability-filter-kind">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos ({events.length})</SelectItem>
                                {kindOptions.map(k => (
                                    <SelectItem key={k} value={k}>{KIND_LABEL[k] || k}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium w-36">Cuando</th>
                                <th className="text-left px-4 py-2 font-medium">Tipo</th>
                                <th className="text-left px-4 py-2 font-medium">Sev</th>
                                <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center text-xs text-slate-400 py-8" data-testid="reliability-timeline-empty">
                                        Sin eventos.
                                    </td>
                                </tr>
                            )}
                            {filtered.map((ev) => {
                                const p = ev.payload || {};
                                const detail = [];
                                if (p.email) detail.push(p.email);
                                if (p.slug) detail.push(`slug=${p.slug}`);
                                if (p.missing_count != null) detail.push(`falta=${p.missing_count}`);
                                if (p.gap_seconds != null) detail.push(`gap=${Math.round(p.gap_seconds)}s`);
                                if (p.reason) detail.push(p.reason);
                                if (p.flag && p.value !== undefined) detail.push(`${p.flag}=${p.value}`);
                                if (p.total_scanned != null) detail.push(`scan=${p.total_scanned}`);
                                if (p.with_gaps != null && p.with_gaps > 0) detail.push(`gaps=${p.with_gaps}`);
                                if (p.errors != null && p.errors > 0) detail.push(`errs=${p.errors}`);
                                if (p.deep) detail.push("deep");
                                return (
                                    <tr key={ev._id || ev.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`reliability-event-${ev.kind}`}>
                                        <td className="px-4 py-2 text-xs text-slate-600 font-mono whitespace-nowrap">
                                            {formatSince(ev.created_at)}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-800">
                                            {KIND_LABEL[ev.kind] || ev.kind}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className={`inline-block text-[10px] px-2 py-0.5 rounded border ${SEV_STYLE[ev.severity] || SEV_STYLE.info}`}>
                                                {ev.severity}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell truncate max-w-[420px]">
                                            {detail.join(" · ") || "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Broken slugs (WS) */}
            {brokenCount > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="reliability-broken-slugs">
                    <div className="text-xs font-semibold text-amber-800 mb-2">Slugs marcados broken (WS)</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                        {Object.entries(ws.broken_slugs || {}).map(([slug, info]) => (
                            <div key={slug} className="text-xs font-mono text-amber-900 truncate">
                                {slug} <span className="text-amber-600 font-sans">— expira {formatSince(info.expires_at)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Phase 15.3 — Permanently broken slugs (persistidos, requieren retry manual) */}
            {permanentSlugsList.length > 0 && (
                <div
                    className="rounded-2xl border border-rose-200 bg-rose-50 p-4"
                    data-testid="reliability-permanently-broken"
                >
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                            <div className="text-xs font-semibold text-rose-800">
                                Slugs desactivados permanentemente ({permanentSlugsList.length})
                            </div>
                            <div className="text-[11px] text-rose-600 mt-0.5">
                                Escalados tras 3 retries fallidos del loop 24h. Ya no se reintentan automáticamente.
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        {permanentSlugsList.map(([slug, info]) => (
                            <div
                                key={slug}
                                className="flex items-center justify-between gap-3 p-2 rounded border border-rose-200 bg-white"
                                data-testid={`permanently-broken-${slug}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono text-xs text-rose-900 truncate">{slug}</div>
                                    <div className="text-[10px] text-rose-700 mt-0.5 truncate">
                                        Marcado {formatSince(info.marked_at)} · {info.retry_count} retries
                                        {info.last_error && ` · ${info.last_error.slice(0, 80)}`}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[11px] shrink-0"
                                    data-testid={`retry-permanent-${slug}`}
                                    onClick={() => retryPermanentSlug(slug)}
                                >
                                    Reintentar manualmente
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {/* Phase 15.4 — Diagnostic modal (endpoint agregado) */}
            <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="reliability-diagnostic-dialog">
                    <DialogHeader>
                        <DialogTitle>Diagnóstico de reliability</DialogTitle>
                        <DialogDescription>
                            Snapshot completa de mensajes, push subs y eventos en la ventana. Sin PII.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-center gap-2 mb-4">
                        <Label className="text-xs text-slate-500">Ventana</Label>
                        <Select
                            value={diagWindow}
                            onValueChange={(v) => { setDiagWindow(v); openDiagnostic(v); }}
                        >
                            <SelectTrigger className="h-8 text-xs w-[160px]" data-testid="reliability-diagnostic-window">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="24h">Últimas 24h</SelectItem>
                                <SelectItem value="7d">Últimos 7 días</SelectItem>
                                <SelectItem value="30d">Últimos 30 días</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {diagLoading && <div className="text-sm text-slate-500 py-8 text-center">Calculando…</div>}
                    {diagError && (
                        <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-3">
                            Error: {diagError}
                        </div>
                    )}

                    {diagData && !diagLoading && (
                        <div className="space-y-4" data-testid="reliability-diagnostic-content">
                            {/* Análisis en texto */}
                            {diagData.analysis?.notes?.length > 0 && (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                    <div className="text-xs font-semibold text-blue-800 mb-2">Diagnóstico</div>
                                    <ul className="space-y-1.5 text-xs text-slate-700 list-disc pl-5">
                                        {diagData.analysis.notes.map((n, i) => (
                                            <li key={i}>{n}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Contadores principales */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">Inbound total</div>
                                    <div className="text-lg font-semibold">{diagData.messages?.inbound_total ?? 0}</div>
                                </div>
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">De cliente</div>
                                    <div className="text-lg font-semibold">{diagData.messages?.from_customer ?? 0}</div>
                                </div>
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">De agent_kimkim</div>
                                    <div className="text-lg font-semibold">{diagData.messages?.from_agent_kimkim ?? 0}</div>
                                </div>
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">Email fallback enviados</div>
                                    <div className="text-lg font-semibold">{diagData.email_fallback?.sent ?? 0}</div>
                                </div>
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">Alertas buzón enviadas</div>
                                    <div className="text-lg font-semibold">{diagData.client_alert?.sent ?? 0}</div>
                                </div>
                                <div className="rounded border border-slate-200 bg-white p-2">
                                    <div className="text-slate-500">Alertas recovery enviadas</div>
                                    <div className="text-lg font-semibold">{diagData.gap_alert?.sent ?? 0}</div>
                                </div>
                                <div className={`rounded border p-2 ${(diagData.broadcasts_without_recipients ?? 0) > 0 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
                                    <div className="text-slate-500">Msgs sin destinatarios</div>
                                    <div className={`text-lg font-semibold ${(diagData.broadcasts_without_recipients ?? 0) > 0 ? "text-amber-700" : ""}`}>
                                        {diagData.broadcasts_without_recipients ?? 0}
                                    </div>
                                </div>
                            </div>

                            {/* Razones de skip del email fallback */}
                            {diagData.email_fallback?.skipped > 0 && (
                                <div className="rounded-lg border border-slate-200 p-3">
                                    <div className="text-xs font-semibold text-slate-700 mb-2">
                                        Razones de email fallback saltado ({diagData.email_fallback.skipped} total)
                                    </div>
                                    <div className="space-y-1">
                                        {Object.entries(diagData.email_fallback.skipped_reasons || {}).map(([r, c]) => (
                                            <div key={r} className="flex justify-between text-xs">
                                                <span className="text-slate-600 font-mono">{r}</span>
                                                <span className="text-slate-800 font-semibold">{c}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Push subs por user */}
                            <div className="rounded-lg border border-slate-200">
                                <div className="text-xs font-semibold text-slate-700 p-3 border-b border-slate-100 flex justify-between">
                                    <span>Push subs por agente</span>
                                    <span className="text-slate-500">
                                        Activas: {diagData.push?.total_active_subs ?? 0} · Zombies: {diagData.push?.total_zombie_subs ?? 0}
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="text-[10px] uppercase tracking-wide text-slate-500 bg-slate-50">
                                            <tr>
                                                <th className="text-left px-3 py-2">Agente</th>
                                                <th className="text-left px-3 py-2">Rol</th>
                                                <th className="text-right px-3 py-2">Total</th>
                                                <th className="text-right px-3 py-2">Activas</th>
                                                <th className="text-right px-3 py-2">Zombies</th>
                                                <th className="text-left px-3 py-2">Últ. uso</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(diagData.push?.subs_by_user || []).map((u) => (
                                                <tr key={u.user_id} className="border-t border-slate-100">
                                                    <td className="px-3 py-1.5 truncate max-w-[180px]">{u.name || u.email}</td>
                                                    <td className="px-3 py-1.5 text-slate-500">{u.role}</td>
                                                    <td className="px-3 py-1.5 text-right font-mono">{u.total_subs}</td>
                                                    <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{u.active_subs}</td>
                                                    <td className={`px-3 py-1.5 text-right font-mono ${u.zombie_subs > 0 ? "text-amber-700" : ""}`}>{u.zombie_subs}</td>
                                                    <td className="px-3 py-1.5 text-slate-500 font-mono text-[10px]">{formatSince(u.last_used_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Events by kind */}
                            <div className="rounded-lg border border-slate-200 p-3">
                                <div className="text-xs font-semibold text-slate-700 mb-2">Eventos por tipo (ventana completa)</div>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                    {Object.entries(diagData.events_by_kind_top || {}).map(([k, c]) => (
                                        <div key={k} className="flex justify-between font-mono text-[11px]">
                                            <span className="text-slate-600">{k}</span>
                                            <span className="text-slate-800 font-semibold">{c}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Wrapper repair confirmation dialog (Phase 15.5) — el operador
                debe confirmar antes de arrancar el job porque puede tocar
                muchos mensajes. Emails de mensajes con más de
                CLIENT_ALERT_MAX_AGE_MINUTES (default 60) NO se dispararán,
                así que reparar históricos no llena el buzón compartido. */}
            <Dialog open={wrapperConfirmOpen} onOpenChange={setWrapperConfirmOpen}>
                <DialogContent data-testid="reliability-wrapper-confirm">
                    <DialogHeader>
                        <DialogTitle>Reparar mensajes wrapper</DialogTitle>
                        <DialogDescription>
                            Escanea los mensajes guardados que son el email
                            de notificación de KimKim ("You've received a new
                            message…") y los reemplaza por el contenido real
                            del chat. Corre en background y se puede seguir
                            desde esta misma página.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-xs text-slate-600 space-y-2 py-2">
                        <div>
                            <b>No se envían alertas por email</b> para
                            wrappers con más de 60 minutos de antigüedad
                            (los históricos quedan silenciados). Solo los
                            wrappers recientes (&lt; 1&nbsp;h) disparan
                            notificación al buzón compartido, como si
                            hubieran llegado en tiempo real.
                        </div>
                        <div>
                            Solo puede haber una reparación en curso a la
                            vez. Si ya hay una, este botón la reutiliza.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setWrapperConfirmOpen(false)}
                            data-testid="reliability-wrapper-confirm-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={startFixWrappers}
                            disabled={wrapperFixing}
                            data-testid="reliability-wrapper-confirm-run"
                        >
                            {wrapperFixing ? "Iniciando…" : "Reparar ahora"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminPage() {
    const { user } = useAuth();
    if (user && user.role !== "admin") {
        return <Navigate to="/inbox" replace />;
    }
    return (
        <AdminShell>
            <Routes>
                <Route index element={<Navigate to="users" replace />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="channels" element={<ChannelsPage />} />
                <Route path="first-touches" element={<FirstTouchesPage />} />
                <Route path="duplicate-trips" element={<DuplicateTripsPage />} />
                <Route path="email-log" element={<EmailLogPage />} />
                <Route path="reliability" element={<ReliabilityPage />} />
                <Route path="simulate" element={<SimulateInboundPage />} />
            </Routes>
        </AdminShell>
    );
}
